import React, { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AssetPrompt, DataDictionaryToggle, DataDictionaryModal } from '../components';
import { H1, SidebarToggleButton } from '../../../components';

import { useAgenticAssetBuilder } from '../providers';
import { usePortal, useSidebar } from '../../../providers';
import { useSources } from '../../organization';
import { useAgenticCreateChatMutation } from '../hooks/useAgenticCreateChatMutation';
import { useAuth } from '../../authentication';

const NewChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`;

const NewChatFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const StyledSidebarToggleButton = styled(SidebarToggleButton)`
  position: absolute;
  top: 20px;
  left: 16px;
`;

const StyledAssetPrompt = styled(AssetPrompt)`
  width: 500px;
`;

const WhatWouldYouLikeToKnow = styled(H1)`
  font-weight: 600;
  margin-bottom: 12px;
`;

const AgenticNewChat = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const insertMentionRef = useRef(null);
  const { accessToken } = useAuth();

  const {
    handleCreateQuestion,
    columnMentionItems,
    databaseDescription,
    termTranslations,
    metrics,
    currentUser,
    matchingEntities,
    findEntityMatches,
    handleSelectEntity,
    currentOrganization
  } = useAgenticAssetBuilder();
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar();
  const { showPortal } = usePortal();
  const { sources } = useSources();
  const { mutateAsync: createChat } = useAgenticCreateChatMutation(accessToken);

  const onOpenDataDictionaryModal = useCallback(() => {
    showPortal(
      <DataDictionaryModal
        databaseDescription={databaseDescription}
        insertMention={insertMentionRef.current}
        sources={sources}
        termTranslations={termTranslations}
        isReadOnly={!currentUser?.canAsk}
      />
    );
  }, [
    showPortal,
    databaseDescription,
    editorRef.current,
    insertMentionRef,
    sources,
    termTranslations
  ]);

  const onCreateNewChat = useCallback(
    async (body) => {
      try {
        const chat = await createChat({
          organizationId: currentOrganization?.id
        });

        navigate(`/agentic-chats/${chat.id}`);
        handleCreateQuestion(body, null, null, chat.id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating agentic chat:', error);
      }
    },
    [createChat, handleCreateQuestion, navigate, currentOrganization?.id]
  );

  return (
    <NewChatContainer>
      {sidebarCollapsed && (
        <StyledSidebarToggleButton collapsed onClick={() => setSidebarCollapsed(false)} />
      )}
      <NewChatFormContainer>
        <WhatWouldYouLikeToKnow>What would you like to know?</WhatWouldYouLikeToKnow>
        <StyledAssetPrompt
          ref={editorRef}
          insertMentionRef={insertMentionRef}
          placeholder='Ask a question...'
          handleCreateQuestion={onCreateNewChat}
          handleSelectEntity={handleSelectEntity}
          sources={sources}
          mentionItems={{
            columns: columnMentionItems,
            metrics
          }}
          matchingEntities={matchingEntities}
          termTranslations={termTranslations}
          findEntityMatches={findEntityMatches}
          {...(databaseDescription &&
            sources?.length > 0 &&
            termTranslations && {
              actions: (
                <DataDictionaryToggle onClick={onOpenDataDictionaryModal} label='Data Dictionary' />
              )
            })}
        />
      </NewChatFormContainer>
    </NewChatContainer>
  );
};

export { AgenticNewChat };
